import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import supabase from "../config/supabaseConfig.js";
import alunosService from "../services/alunos.service";
import professoresService from "../services/professores.service";
import aulasService, { getDataAtual } from "../services/aulas.service";
import aulaAlunosService from "../services/AulaAlunosService";
import { useAuth } from "../hooks/useAuth";
import { getViewAs } from "../lib/viewAs";
import "../styles/PresalaNova.css";

// Pré-sala da recepção (patch 2026) — layout de docs/prototipos/presala.html.
// Fluxo real (regra validada em reunião 12/06): recepção marca quem está
// presente → sistema DISTRIBUI ALEATORIAMENTE entre os professores do dia →
// distribuição é EDITÁVEL (Alex/ADM remaneja casos complexos antes de
// liberar) → "Liberar" abre os alunos na conta de cada professor de verdade.
//
// Sem migration nova: reusa o modelo atual (1 aula em_andamento por
// professor). Liberar = achar/criar a aula do dia de cada professor e
// inserir os alunos via aula_alunos — o mesmo caminho que a Sala usa.

const temRestricao = (aluno) =>
  aluno.lesao && aluno.lesao !== "Nao" && aluno.lesao !== "Não";

const iniciais = (nome = "") =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

// Fisher-Yates: embaralha uma cópia (sem mutar o original).
const embaralhar = (ids) => {
  const arr = [...ids];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Segunda-feira da semana corrente em DATA LOCAL do browser, "YYYY-MM-DD".
// ⚠ pegadinha UTC do projeto: new Date().toISOString() vira o dia SEGUINTE
// depois das 21h BRT — por isso montamos a string com getFullYear/Month/Date
// (data local), NUNCA via toISOString. Semana = segunda→domingo, o mesmo
// recorte do reset de volume (dom 23:59 BRT).
const inicioDaSemanaLocal = () => {
  const hoje = new Date();
  const diaSemana = hoje.getDay(); // 0=domingo … 6=sábado (fuso local)
  // Quantos dias voltar até a segunda: domingo(0) volta 6, os demais dia-1.
  const voltar = (diaSemana + 6) % 7;
  // new Date(ano, mês, dia-voltar) normaliza virada de mês/ano sozinho.
  const segunda = new Date(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate() - voltar
  );
  const yyyy = segunda.getFullYear();
  const mm = String(segunda.getMonth() + 1).padStart(2, "0");
  const dd = String(segunda.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const PresalaNova = () => {
  const { user, isAdmin } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [liberando, setLiberando] = useState(false);
  const [liberado, setLiberado] = useState(false);
  // Liberar NÃO encerra a edição (Tales 08/07): o quadro continua ajustável
  // e cada movimento reflete na aula real do professor (aula_alunos).
  // Decisão Rodrigo 14/07: RECEPÇÃO também ajusta pós-liberação (com o
  // dropdown da Sala removido, a Pré-sala é o único lugar de remanejo).
  // Respeita "Ver como" pro admin testar os outros papéis.
  const viewAs = isAdmin ? getViewAs(user?.email) : null;
  const papelEfetivo = viewAs?.role || user?.role;
  const podeAjustarPosLiberacao = ["admin", "lider", "recepcao"].includes(
    papelEfetivo
  );
  const edicaoBloqueada = liberado && !podeAjustarPosLiberacao;
  // profId -> aulaId real criada/achada no liberar (base dos ajustes pós).
  const [aulaPorProf, setAulaPorProf] = useState({});

  const [todosAlunos, setTodosAlunos] = useState([]);
  const [todosProfessores, setTodosProfessores] = useState([]);

  const [buscaAluno, setBuscaAluno] = useState("");
  const [poolIds, setPoolIds] = useState([]); // presentes ainda não distribuídos
  const [profsSelecionados, setProfsSelecionados] = useState([]); // ids do dia
  // Map professorId -> [alunoId]
  const [distribuicao, setDistribuicao] = useState({});
  // Botão 🎲 em loading enquanto consulta o histórico da semana.
  const [redistribuindo, setRedistribuindo] = useState(false);
  // Histórico da semana (segunda→hoje) dos alunos do quadro:
  // Map alunoId -> Set profId. Base do aviso de "professor repetido", que
  // vale pra QUALQUER posição do aluno (🎲, arrasto, dropdown, reidratação) —
  // pedido Rodrigo 02/10: a recepção remaneja na mão e às vezes não percebe
  // que repetiu. Exclui as aulas desta própria rodada (aulaPorProf), senão
  // pós-liberação todo aluno apareceria "repetido" com o próprio professor.
  const [historicoSemana, setHistoricoSemana] = useState(new Map());

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setCarregando(true);
        const [alunos, professores] = await Promise.all([
          alunosService.getAll(),
          professoresService.getAll(),
        ]);
        if (!ativo) return;
        setTodosAlunos((alunos || []).filter((a) => a.status === "ativo"));
        setTodosProfessores(professores || []);

        // Reidratar o quadro: as aulas em_andamento SÃO a distribuição
        // liberada. Sem isso, sair da Pré-sala (ex.: abrir o Telão na mesma
        // aba) zerava o quadro pra quem voltasse — a recepção "perdia" as
        // aulas mesmo com tudo vivo no banco. Mesmo critério da Sala e do
        // acharOuCriarAula: em_andamento, mais recente por professor.
        const { data: aulasAbertas, error: aulasError } = await supabase
          .from("aulas")
          .select("id, professor_id, created_at")
          .eq("status", "em_andamento")
          .order("created_at", { ascending: false });
        if (aulasError) throw aulasError;
        const aulaDoProf = {};
        (aulasAbertas || []).forEach((a) => {
          if (!aulaDoProf[a.professor_id]) aulaDoProf[a.professor_id] = a.id;
        });
        const aulaIds = Object.values(aulaDoProf);
        if (aulaIds.length > 0) {
          const { data: vinculos, error: vinculosError } = await supabase
            .from("aula_alunos")
            .select("aula_id, aluno_id")
            .in("aula_id", aulaIds);
          if (vinculosError) throw vinculosError;
          if (!ativo) return;
          const dist = {};
          Object.entries(aulaDoProf).forEach(([profId, aulaId]) => {
            dist[profId] = (vinculos || [])
              .filter((v) => v.aula_id === aulaId)
              .map((v) => v.aluno_id);
          });
          setProfsSelecionados(Object.keys(aulaDoProf));
          setDistribuicao(dist);
          setAulaPorProf(aulaDoProf);
          setLiberado(true);
        }
      } catch (err) {
        console.error("Erro ao carregar pré-sala:", err);
        if (ativo) setErro("Erro ao carregar dados: " + err.message);
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Chaves estáveis pro efeito do histórico: só refaz a consulta quando muda
  // QUEM está no quadro ou as aulas da rodada — arrastar entre professores
  // não muda nada disso, então não dispara query.
  const chaveAlunosQuadro = useMemo(
    () =>
      [...poolIds, ...Object.values(distribuicao).flat()].sort().join(","),
    [poolIds, distribuicao]
  );
  const chaveAulasRodada = Object.values(aulaPorProf).sort().join(",");

  useEffect(() => {
    if (!chaveAlunosQuadro) {
      setHistoricoSemana(new Map());
      return undefined;
    }
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("aula_alunos")
          .select("aluno_id, aula_id, aulas!inner(professor_id, data)")
          .in("aluno_id", chaveAlunosQuadro.split(","))
          .gte("aulas.data", inicioDaSemanaLocal())
          // Só conta quem TREINOU (02/10): falta e aula cancelada não
          // contam como "já teve aula com esse professor".
          .eq("status", "presente")
          .neq("aulas.status", "cancelada");
        if (error) throw error;
        if (!ativo) return;
        const aulasDaRodada = new Set(
          chaveAulasRodada ? chaveAulasRodada.split(",") : []
        );
        const mapa = new Map();
        (data || []).forEach((row) => {
          const profId = row.aulas?.professor_id;
          if (!profId || aulasDaRodada.has(row.aula_id)) return;
          if (!mapa.has(row.aluno_id)) mapa.set(row.aluno_id, new Set());
          mapa.get(row.aluno_id).add(profId);
        });
        setHistoricoSemana(mapa);
      } catch (err) {
        // Aviso é só informativo: se falhar, o quadro segue sem ele.
        console.error("Histórico da semana indisponível:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [chaveAlunosQuadro, chaveAulasRodada]);

  const jaTeveAulaNaSemana = (alunoId, profId) =>
    historicoSemana.get(alunoId)?.has(profId) || false;

  const alunosPorId = useMemo(
    () => new Map(todosAlunos.map((a) => [a.id, a])),
    [todosAlunos]
  );
  const professoresPorId = useMemo(
    () => new Map(todosProfessores.map((p) => [p.id, p])),
    [todosProfessores]
  );

  const idsJaEmUso = useMemo(() => {
    const s = new Set(poolIds);
    Object.values(distribuicao).forEach((lista) =>
      lista.forEach((id) => s.add(id))
    );
    return s;
  }, [poolIds, distribuicao]);

  const resultadosBusca = useMemo(() => {
    const termo = buscaAluno.trim().toLowerCase();
    if (!termo) return [];
    return todosAlunos
      .filter(
        (a) =>
          !idsJaEmUso.has(a.id) && a.nome.toLowerCase().includes(termo)
      )
      .slice(0, 8);
  }, [buscaAluno, todosAlunos, idsJaEmUso]);

  const adicionarAoPool = (alunoId) => {
    setPoolIds((prev) => [...prev, alunoId]);
    setBuscaAluno("");
  };

  const removerDoPool = (alunoId) => {
    setPoolIds((prev) => prev.filter((id) => id !== alunoId));
  };

  const toggleProfessorDoDia = (profId) => {
    // Pós-liberação não mexe no time do dia (tiraria alunos de aula real
    // sem sync) — ajuste fino é por aluno; recomeço é a "Nova rodada".
    if (liberado) return;
    setProfsSelecionados((prev) =>
      prev.includes(profId)
        ? prev.filter((id) => id !== profId)
        : [...prev, profId]
    );
    // Se remover um professor que já tinha alunos, devolve pro pool.
    setDistribuicao((prev) => {
      if (!prev[profId]) return prev;
      const { [profId]: removidos, ...resto } = prev;
      setPoolIds((p) => [...p, ...removidos]);
      return resto;
    });
  };

  // Sorteio puro (comportamento antigo): Fisher-Yates + round-robin, sem
  // memória de histórico. Reusado como FALLBACK quando o histórico da
  // semana não pôde ser consultado — o botão nunca pode travar a recepção.
  const sorteioPuro = (todosIds) => {
    const embaralhados = embaralhar(todosIds);
    const novaDistribuicao = {};
    profsSelecionados.forEach((id) => (novaDistribuicao[id] = []));
    embaralhados.forEach((alunoId, idx) => {
      const profId = profsSelecionados[idx % profsSelecionados.length];
      novaDistribuicao[profId].push(alunoId);
    });
    setDistribuicao(novaDistribuicao);
    setPoolIds([]);
  };

  // 🎲 Redistribuir com RODÍZIO: consulta o histórico aluno×professor da
  // semana (segunda→hoje) e evita repetir o mesmo par. Pedido do Tales no
  // go-live 22/07. Se o histórico não puder ser lido, cai no sorteio puro.
  const redistribuir = async () => {
    if (profsSelecionados.length === 0) return;
    const todosIds = [...poolIds, ...Object.values(distribuicao).flat()];
    if (todosIds.length === 0) return;

    setRedistribuindo(true);
    try {
      // 1) Histórico da semana: pares aluno×professor via aula_alunos com a
      //    aula embutida (professor_id + data). !inner descarta vínculo sem
      //    aula; .gte filtra pela data local da segunda (não UTC).
      const segunda = inicioDaSemanaLocal();
      // Map alunoId -> Map profId -> dataMaisRecente (string "YYYY-MM-DD").
      let historico;
      try {
        const { data, error } = await supabase
          .from("aula_alunos")
          .select("aluno_id, aulas!inner(professor_id, data)")
          // Só os alunos desta distribuição: sem o .in, a semana da academia
          // inteira estoura o limite de 1000 linhas do PostgREST e o
          // histórico vem TRUNCADO em silêncio (rodízio decidiria errado).
          .in("aluno_id", todosIds)
          .gte("aulas.data", segunda)
          // Mesmo critério do aviso: só presença conta (falta/cancelada não).
          .eq("status", "presente")
          .neq("aulas.status", "cancelada");
        if (error) throw error;
        historico = new Map();
        (data || []).forEach((row) => {
          const profId = row.aulas?.professor_id;
          const dataAula = row.aulas?.data;
          if (!profId || !dataAula) return;
          if (!historico.has(row.aluno_id)) historico.set(row.aluno_id, new Map());
          const porProf = historico.get(row.aluno_id);
          const atual = porProf.get(profId);
          // Guarda a MAIS RECENTE (strings YYYY-MM-DD comparam bem lexicalmente).
          if (!atual || dataAula > atual) porProf.set(profId, dataAula);
        });
      } catch (err) {
        // Resiliência (RLS/rede/o que for): sorteio puro + aviso, sem travar.
        console.error("Rodízio indisponível, caindo no sorteio puro:", err);
        toast.warn(
          "Rodízio da semana indisponível — distribuição feita por sorteio simples."
        );
        sorteioPuro(todosIds);
        return;
      }

      // 2) Embaralha os alunos (mantém o Fisher-Yates).
      const embaralhados = embaralhar(todosIds);

      // 3) Um a um, cada aluno vai pro professor que evita repetição e mantém
      //    o equilíbrio.
      const novaDistribuicao = {};
      profsSelecionados.forEach((id) => (novaDistribuicao[id] = []));

      embaralhados.forEach((alunoId) => {
        const historicoDoAluno = historico.get(alunoId) || new Map();
        // Candidatos: profs do dia com quem o aluno NÃO teve aula na semana.
        const candidatos = profsSelecionados.filter(
          (pid) => !historicoDoAluno.has(pid)
        );
        let escolhido;
        if (candidatos.length > 0) {
          // Menor grupo entre os candidatos (empate = o primeiro na ordem) —
          // mantém as turmas equilibradas sem repetir.
          escolhido = candidatos.reduce(
            (melhor, pid) =>
              novaDistribuicao[pid].length < novaDistribuicao[melhor].length
                ? pid
                : melhor,
            candidatos[0]
          );
        } else {
          // Fallback: já passou por TODOS os profs do dia na semana → vai pro
          // da aula mais ANTIGA (menos recente) e ganha o marcador de repetido.
          escolhido = profsSelecionados.reduce((melhor, pid) =>
            historicoDoAluno.get(pid) < historicoDoAluno.get(melhor)
              ? pid
              : melhor
          );
        }
        novaDistribuicao[escolhido].push(alunoId);
      });

      setDistribuicao(novaDistribuicao);
      setPoolIds([]);
    } finally {
      setRedistribuindo(false);
    }
  };

  // Acha a aula em_andamento do professor hoje ou cria uma — mesmo caminho
  // do liberar (select direto, sem embed "alunos(*)" que colide com a
  // coluna jsonb, ver comentário no liberarParaProfessores).
  const acharOuCriarAula = async (profId) => {
    const { data: abertas, error: buscaError } = await supabase
      .from("aulas")
      .select("id")
      .eq("professor_id", profId)
      .eq("status", "em_andamento")
      .order("created_at", { ascending: false })
      .limit(1);
    if (buscaError) throw buscaError;
    let aulaId = abertas?.[0]?.id;
    if (!aulaId) {
      const novaAula = await aulasService.create({
        professor_id: profId,
        data: getDataAtual(),
        status: "em_andamento",
      });
      aulaId = novaAula.id;
    }
    return aulaId;
  };

  // Pós-liberação: espelha o movimento do quadro na aula real do professor.
  const sincronizarMovimento = async (alunoId, deProfId, paraProfId) => {
    try {
      if (deProfId && aulaPorProf[deProfId]) {
        await aulaAlunosService.removerAluno(aulaPorProf[deProfId], alunoId);
      }
      if (paraProfId) {
        let aulaId = aulaPorProf[paraProfId];
        if (!aulaId) {
          aulaId = await acharOuCriarAula(paraProfId);
          setAulaPorProf((prev) => ({ ...prev, [paraProfId]: aulaId }));
        }
        await aulaAlunosService.adicionarAluno(aulaId, alunoId);
      }
    } catch (err) {
      console.error("Erro ao sincronizar ajuste pós-liberação:", err);
      setErro("Erro ao aplicar o ajuste na sala do professor: " + err.message);
    }
  };

  const profDoAluno = (alunoId) =>
    Object.keys(distribuicao).find((pid) =>
      (distribuicao[pid] || []).includes(alunoId)
    ) || null;

  const moverParaProfessor = (alunoId, profId) => {
    if (edicaoBloqueada) return;
    const origem = profDoAluno(alunoId);
    setPoolIds((prev) => prev.filter((id) => id !== alunoId));
    setDistribuicao((prev) => {
      const semAluno = Object.fromEntries(
        Object.entries(prev).map(([pid, lista]) => [
          pid,
          lista.filter((id) => id !== alunoId),
        ])
      );
      return {
        ...semAluno,
        [profId]: [...(semAluno[profId] || []), alunoId],
      };
    });
    if (liberado && origem !== profId) {
      sincronizarMovimento(alunoId, origem, profId);
    }
  };

  const removerDeProfessor = (alunoId, profId) => {
    if (edicaoBloqueada) return;
    setDistribuicao((prev) => ({
      ...prev,
      [profId]: (prev[profId] || []).filter((id) => id !== alunoId),
    }));
    setPoolIds((prev) => [...prev, alunoId]);
    if (liberado) {
      sincronizarMovimento(alunoId, profId, null);
    }
  };

  // ── Drag & drop (feedback Tales 07/07; reescrito 17/07): arrastar aluno
  // do pool pro professor, entre professores e de volta pro pool. POINTER
  // EVENTS unificados (mouse E touch) — o HTML5 DnD original não dispara em
  // navegador touch (iPad/celular). Threshold de 8px separa tap de arrasto;
  // ghost segue o ponteiro; alvo por elementFromPoint em [data-drop]. O
  // dropdown "Atribuir a…" continua como alternativa sem arrasto.
  const [dragOver, setDragOver] = useState(null); // profId | "pool" | null

  const moverParaPool = (alunoId) => {
    if (edicaoBloqueada) return;
    const origem = profDoAluno(alunoId);
    setDistribuicao((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([pid, lista]) => [
          pid,
          lista.filter((id) => id !== alunoId),
        ])
      )
    );
    setPoolIds((prev) =>
      prev.includes(alunoId) ? prev : [...prev, alunoId]
    );
    if (liberado && origem) {
      sincronizarMovimento(alunoId, origem, null);
    }
  };

  // Ghost do arrasto ({alunoId, nome, x, y} | null) — null = sem arrasto.
  const [drag, setDrag] = useState(null);
  const dragInfoRef = useRef(null);

  const alvoSob = (x, y) => {
    const el = document.elementFromPoint(x, y);
    return el?.closest("[data-drop]")?.getAttribute("data-drop") || null;
  };

  const iniciarDrag = (e, alunoId) => {
    if (edicaoBloqueada) return;
    // Botão direito e toques nos controles do chip (×, select) não arrastam.
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest("button, select, input")) return;
    const nome = alunosPorId.get(alunoId)?.nome || "";
    const info = {
      alunoId,
      nome,
      startX: e.clientX,
      startY: e.clientY,
      ativo: false,
    };

    const onMove = (ev) => {
      const dx = ev.clientX - info.startX;
      const dy = ev.clientY - info.startY;
      if (!info.ativo && dx * dx + dy * dy < 64) return; // 8px de threshold
      info.ativo = true;
      setDrag({ alunoId, nome, x: ev.clientX, y: ev.clientY });
      setDragOver(alvoSob(ev.clientX, ev.clientY));
    };

    const encerrar = (ev, cancelado) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      dragInfoRef.current = null;
      setDrag(null);
      setDragOver(null);
      if (cancelado || !info.ativo) return; // tap simples: nada a fazer
      const destino = alvoSob(ev.clientX, ev.clientY);
      if (!destino) return;
      if (destino === "pool") moverParaPool(alunoId);
      else moverParaProfessor(alunoId, destino);
    };
    const onUp = (ev) => encerrar(ev, false);
    const onCancel = (ev) => encerrar(ev, true);

    dragInfoRef.current = info;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  // Alunos no quadro com professor já visto na semana E que teriam outro
  // professor do dia "novo" pra ele — esses dá pra trocar; os demais
  // (já passaram por todos) só ficam marcados no chip, sem cobrar ação.
  const repetidosEvitaveis = useMemo(() => {
    const lista = [];
    Object.entries(distribuicao).forEach(([profId, alunoIds]) => {
      alunoIds.forEach((alunoId) => {
        const vistos = historicoSemana.get(alunoId);
        if (!vistos?.has(profId)) return;
        if (profsSelecionados.some((pid) => !vistos.has(pid))) {
          lista.push(alunoId);
        }
      });
    });
    return lista;
  }, [distribuicao, historicoSemana, profsSelecionados]);

  const totalDistribuidos = Object.values(distribuicao).flat().length;
  const totalPresentes = poolIds.length + totalDistribuidos;

  const liberarParaProfessores = async () => {
    const entradas = Object.entries(distribuicao).filter(
      ([, lista]) => lista.length > 0
    );
    if (entradas.length === 0) return;
    try {
      setLiberando(true);
      setErro(null);
      const novoAulaPorProf = {};
      for (const [profId, alunoIds] of entradas) {
        // eslint-disable-next-line no-await-in-loop
        const aulaId = await acharOuCriarAula(profId);
        novoAulaPorProf[profId] = aulaId;
        for (const alunoId of alunoIds) {
          // eslint-disable-next-line no-await-in-loop
          await aulaAlunosService.adicionarAluno(aulaId, alunoId);
        }
      }
      // Feedback Tales 08/07: o quadro NÃO é limpo — admin/líder seguem
      // ajustando e cada movimento sincroniza com a aula do professor.
      setAulaPorProf(novoAulaPorProf);
      setLiberado(true);
    } catch (err) {
      console.error("Erro ao liberar pré-sala:", err);
      setErro("Erro ao liberar para os professores: " + err.message);
    } finally {
      setLiberando(false);
    }
  };

  const novaRodada = () => {
    setLiberado(false);
    setDistribuicao({});
    setPoolIds([]);
    setAulaPorProf({});
  };

  if (carregando) {
    return (
      <div className="presala-nova">
        <div className="ps-header">
          <h1>Pré-sala</h1>
        </div>
        <p className="ps-note">Carregando alunos e professores…</p>
      </div>
    );
  }

  return (
    <div className="presala-nova">
      <div className="ps-header">
        <div>
          <h1>Distribuição da turma</h1>
          <span className="ps-sub">Pré-sala · Recepção</span>
        </div>
        <div className="ps-toolbar">
          <button
            className="ps-btn-ghost"
            onClick={redistribuir}
            disabled={
              profsSelecionados.length === 0 ||
              totalPresentes === 0 ||
              liberando ||
              redistribuindo ||
              // Pós-liberação só ajuste fino por aluno (sync individual);
              // re-embaralhar tudo dessincronizaria as aulas reais.
              liberado
            }
          >
            {redistribuindo ? "Redistribuindo…" : "🎲 Redistribuir"}
          </button>
          <button
            className="ps-btn-primary"
            onClick={liberarParaProfessores}
            disabled={totalDistribuidos === 0 || liberando || liberado}
          >
            {liberando
              ? "Liberando…"
              : liberado
              ? "✓ Liberado"
              : "✓ Liberar para professores"}
          </button>
        </div>
      </div>

      <p className="ps-tag-note">
        Divisão automática e aleatória entre os professores do dia. Ajuste
        manualmente <strong>arrastando o aluno</strong> pro card do professor
        (ou de volta pra lista de presentes) antes de liberar — ao liberar,
        cada aluno abre na conta real do professor (a Sala dele já mostra o
        card).
      </p>

      {erro && <div className="ps-erro">{erro}</div>}
      {repetidosEvitaveis.length > 0 && (
        <div className="ps-aviso-repetido">
          <strong>
            {repetidosEvitaveis.length === 1
              ? "1 aluno está"
              : `${repetidosEvitaveis.length} alunos estão`}{" "}
            com um professor que já deu aula pra ele nesta semana
          </strong>{" "}
          ({repetidosEvitaveis
            .map((id) => alunosPorId.get(id)?.nome)
            .filter(Boolean)
            .join(", ")}
          ). Há outro professor do dia disponível — arraste pra trocar.
        </div>
      )}
      {liberado && (
        <div className="ps-sucesso">
          ✓ Distribuição liberada! Os alunos já aparecem na Sala de cada
          professor.{" "}
          {podeAjustarPosLiberacao
            ? "Você ainda pode remanejar alunos — cada ajuste atualiza a sala do professor na hora."
            : "A distribuição agora só pode ser ajustada por admin/líder/recepção."}{" "}
          <button className="ps-link" onClick={novaRodada}>
            Nova rodada
          </button>
        </div>
      )}

      <div className="ps-grid">
        {/* Coluna: presentes (pool) — também é alvo de drop (devolve o
            aluno arrastado de um professor pro pool). */}
        <div
          className={`ps-card ps-presentes ${
            dragOver === "pool" ? "drag-over" : ""
          }`}
          data-drop="pool"
        >
          <div className="ps-overline">Presentes · {totalPresentes}</div>

          <div className="ps-busca">
            <input
              type="text"
              placeholder="Adicionar aluno presente"
              value={buscaAluno}
              onChange={(e) => setBuscaAluno(e.target.value)}
            />
            {resultadosBusca.length > 0 && (
              <div className="ps-busca-resultados">
                {resultadosBusca.map((a) => (
                  <button
                    key={a.id}
                    className="ps-busca-item"
                    onClick={() => adicionarAoPool(a.id)}
                  >
                    {a.nome}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ps-pool-lista">
            {poolIds.length === 0 ? (
              <p className="ps-note">
                {totalPresentes === 0
                  ? "Nenhum aluno adicionado ainda."
                  : "Todos os presentes já foram distribuídos."}
              </p>
            ) : (
              poolIds.map((id) => {
                const aluno = alunosPorId.get(id);
                if (!aluno) return null;
                return (
                  <div
                    key={id}
                    className={`ps-aluno ${
                      temRestricao(aluno) ? "flag" : ""
                    }`}
                    onPointerDown={(e) => iniciarDrag(e, id)}
                  >
                    {temRestricao(aluno) && (
                      <span className="ps-flag-ico" title={aluno.tipo_lesao}>
                        ⚠
                      </span>
                    )}
                    <span className="ps-nome">{aluno.nome}</span>
                    <span className="ps-badge">{aluno.plano}</span>
                    {!edicaoBloqueada && profsSelecionados.length > 0 && (
                      <select
                        className="ps-mini-select"
                        value=""
                        onChange={(e) =>
                          e.target.value &&
                          moverParaProfessor(id, e.target.value)
                        }
                      >
                        <option value="">Atribuir a…</option>
                        {profsSelecionados.map((pid) => (
                          <option key={pid} value={pid}>
                            {professoresPorId.get(pid)?.nome}
                          </option>
                        ))}
                      </select>
                    )}
                    {!edicaoBloqueada && (
                      <button
                        className="ps-x"
                        onClick={() => removerDoPool(id)}
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <hr />
          <div className="ps-overline">Professores do dia</div>
          <div className="ps-profs-picker">
            {todosProfessores.map((p) => (
              <button
                key={p.id}
                className={`ps-prof-chip ${
                  profsSelecionados.includes(p.id) ? "active" : ""
                }`}
                onClick={() => toggleProfessorDoDia(p.id)}
                disabled={liberado}
              >
                {p.nome}
              </button>
            ))}
          </div>
        </div>

        {/* Coluna: cards de professor */}
        <div>
          {profsSelecionados.length === 0 ? (
            <div className="ps-card">
              <p className="ps-note">
                Selecione os professores do dia ao lado para começar a
                distribuir.
              </p>
            </div>
          ) : (
            <div className="ps-profs">
              {profsSelecionados.map((profId) => {
                const prof = professoresPorId.get(profId);
                const alunoIds = distribuicao[profId] || [];
                return (
                  <div
                    key={profId}
                    className={`ps-prof ${
                      dragOver === profId ? "drag-over" : ""
                    }`}
                    data-drop={profId}
                  >
                    <div className="ps-prof-head">
                      <span className="ps-prof-av">
                        {iniciais(prof?.nome)}
                      </span>
                      <span className="ps-prof-nome">{prof?.nome}</span>
                      <span className="ps-prof-cnt">{alunoIds.length}</span>
                    </div>
                    <div className="ps-prof-body">
                      {alunoIds.length === 0 ? (
                        <p className="ps-note ps-note-sm">Sem alunos ainda</p>
                      ) : (
                        alunoIds.map((id) => {
                          const aluno = alunosPorId.get(id);
                          if (!aluno) return null;
                          const repetido = jaTeveAulaNaSemana(id, profId);
                          return (
                            <div
                              key={id}
                              className={`ps-chip ${
                                temRestricao(aluno) ? "flag" : ""
                              }`}
                              onPointerDown={(e) => iniciarDrag(e, id)}
                              title={
                                repetido
                                  ? "Já teve aula com este professor nesta semana"
                                  : undefined
                              }
                            >
                              {temRestricao(aluno) && (
                                <span className="ps-flag-ico">⚠</span>
                              )}
                              {aluno.nome}
                              {repetido && (
                                <span className="ps-repetido-tag">
                                  repetido
                                </span>
                              )}
                              {!edicaoBloqueada && (
                                <button
                                  className="ps-x"
                                  onClick={() =>
                                    removerDeProfessor(id, profId)
                                  }
                                  aria-label="Remover"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="ps-tag-note">
            ⚠ alunos com restrição aparecem destacados — evite concentrar
            casos complexos no mesmo professor.
          </p>
        </div>
      </div>

      {/* Ghost do arrasto (segue o ponteiro; sem eventos próprios pra não
          atrapalhar o elementFromPoint do hit-test). */}
      {drag && (
        <div
          className="ps-drag-ghost"
          style={{ left: drag.x, top: drag.y }}
        >
          {drag.nome}
        </div>
      )}
    </div>
  );
};

export default PresalaNova;
